// M9 Boxing — punch detection (jab / hook / uppercut).
// Spec: /microgames/09-boxing.md
// Algorithm port: lux_punch — wrist kinematics (speed + accel) + dominant-axis classification.

import type {
  GameContext,
  HandFrame,
  InputFrame,
  Microgame,
  MicrogameResult,
  Point,
  PoseFrame,
} from '@pose-game/core';

const DURATION_SEC = 32;
const WIN_HITS = 8;
const TARGET_GLOW_SEC = 1.3;
const TARGET_COOLDOWN_SEC = 0.6;
const REARM_SEC = 0.8;
const TRAIL_LEN = 8;

const SPEED_THRESHOLD = 500; // px/s — easier to register a punch
const ACCEL_THRESHOLD = 2500; // px/s²
const UPPERCUT_VY_THRESHOLD = -500; // negative because canvas y grows downward
const HOOK_LATERAL_RATIO = 0.55;
const JAB_EXTENSION_RATE = 380; // px/s growth in wrist→shoulder distance

const LEFT_SHOULDER = 11;
const RIGHT_SHOULDER = 12;

type TargetKind = 'jab' | 'hook-left' | 'hook-right' | 'uppercut';
type PunchKind = 'jab' | 'hook-left' | 'hook-right' | 'uppercut';
type HandSide = 'left' | 'right';

interface Target {
  kind: TargetKind;
  spawnedAt: number;
  hit: boolean;
}

interface FlashEffect {
  x: number;
  y: number;
  startedAt: number;
  color: string;
}

interface HandState {
  prevVel: Point;
  prevWristToShoulder: number;
  hasPrev: boolean;
  lastFireAt: number;
  trail: Point[];
}

const TARGET_KINDS: TargetKind[] = ['jab', 'hook-left', 'hook-right', 'uppercut'];

// Clay-fruit sprite per punch target (PNGs in /public/assets/fruits/).
const TARGET_SPRITE: Record<TargetKind, string> = {
  jab: 'apple',
  uppercut: 'banana',
  'hook-left': 'watermelon',
  'hook-right': 'pineapple',
};
const PUNCH_SPRITES: Record<TargetKind, HTMLImageElement> = (() => {
  const out = {} as Record<TargetKind, HTMLImageElement>;
  for (const k of TARGET_KINDS) {
    const img = new Image();
    img.src = `/assets/fruits/${TARGET_SPRITE[k]}.png`;
    out[k] = img;
  }
  return out;
})();

export const boxing: Microgame = (() => {
  let game: GameContext | null = null;
  let canvasW = 0;
  let canvasH = 0;
  let elapsed = 0;
  let hits = 0;
  let missed = 0;
  let combo = 0;
  let maxCombo = 0;
  let currentTarget: Target | null = null;
  let lastTargetEndAt = -10;
  const flashes: FlashEffect[] = [];

  const makeHandState = (): HandState => ({
    prevVel: { x: 0, y: 0 },
    prevWristToShoulder: 0,
    hasPrev: false,
    lastFireAt: -10,
    trail: [],
  });

  let lh: HandState = makeHandState();
  let rh: HandState = makeHandState();

  const rand = (): number => (game?.rng ? game.rng() : Math.random());

  const targetPos = (kind: TargetKind): Point => {
    const cx = canvasW / 2;
    const cy = canvasH * 0.5;
    const bagW = Math.min(canvasW, canvasH) * 0.28;
    switch (kind) {
      case 'jab':
        return { x: cx, y: cy };
      case 'hook-left':
        return { x: cx - bagW * 0.95, y: cy };
      case 'hook-right':
        return { x: cx + bagW * 0.95, y: cy };
      case 'uppercut':
        return { x: cx, y: cy - bagW * 0.95 };
    }
  };

  const targetRadius = (): number => Math.min(canvasW, canvasH) * 0.085;

  const targetColor = (kind: TargetKind): string => {
    switch (kind) {
      case 'jab':
        return '#ff5050';
      case 'hook-left':
      case 'hook-right':
        return '#facc15';
      case 'uppercut':
        return '#3b82f6';
    }
  };

  const shoulderFor = (side: HandSide, pose: PoseFrame): Point | null => {
    if (!pose.visible || pose.landmarks.length < 13) return null;
    const idx = side === 'left' ? LEFT_SHOULDER : RIGHT_SHOULDER;
    const lm = pose.landmarks[idx];
    return lm ? { x: lm.x, y: lm.y } : null;
  };

  const updateTrail = (state: HandState, wrist: Point): void => {
    state.trail.push({ x: wrist.x, y: wrist.y });
    while (state.trail.length > TRAIL_LEN) state.trail.shift();
  };

  const fireHit = (kind: PunchKind, hand: HandSide, wrist: Point): void => {
    const state = hand === 'left' ? lh : rh;
    state.lastFireAt = elapsed;
    if (currentTarget != null && !currentTarget.hit && currentTarget.kind === kind) {
      currentTarget.hit = true;
      hits++;
      combo++;
      if (combo > maxCombo) maxCombo = combo;
      const pos = targetPos(currentTarget.kind);
      flashes.push({
        x: pos.x,
        y: pos.y,
        startedAt: elapsed,
        color: '#ffffff',
      });
      game?.audio.pop();
    } else {
      flashes.push({
        x: wrist.x,
        y: wrist.y,
        startedAt: elapsed,
        color: '#7d8298',
      });
      game?.audio.thud();
      combo = 0;
    }
  };

  const classifyPunch = (
    side: HandSide,
    frame: InputFrame,
    dt: number,
  ): void => {
    const hand: HandFrame = frame.hands[side];
    const state = side === 'left' ? lh : rh;

    if (!hand.visible || hand.landmarks.length === 0) {
      state.hasPrev = false;
      if (state.trail.length > 0) state.trail.shift();
      return;
    }
    const wrist = hand.landmarks[0];
    if (!wrist) {
      state.hasPrev = false;
      return;
    }

    updateTrail(state, wrist);

    const shoulder = shoulderFor(side, frame.pose);
    const wristToShoulder = shoulder
      ? Math.hypot(wrist.x - shoulder.x, wrist.y - shoulder.y)
      : 0;

    const vx = hand.wristVelocity.x;
    const vy = hand.wristVelocity.y;
    const speed = Math.hypot(vx, vy);

    let ax = 0;
    let ay = 0;
    let extensionRate = 0;
    if (state.hasPrev && dt > 0) {
      ax = (vx - state.prevVel.x) / dt;
      ay = (vy - state.prevVel.y) / dt;
      if (shoulder) {
        extensionRate = (wristToShoulder - state.prevWristToShoulder) / dt;
      }
    }
    const accel = Math.hypot(ax, ay);

    const inRearm = elapsed - state.lastFireAt < REARM_SEC;

    if (
      !inRearm &&
      state.hasPrev &&
      speed > SPEED_THRESHOLD &&
      accel > ACCEL_THRESHOLD
    ) {
      const absVx = Math.abs(vx);
      const absVy = Math.abs(vy);
      const total = absVx + absVy || 1;
      const lateralRatio = absVx / total;

      let kind: PunchKind | null = null;

      if (vy < UPPERCUT_VY_THRESHOLD && absVy > absVx) {
        kind = 'uppercut';
      } else if (
        lateralRatio > HOOK_LATERAL_RATIO &&
        absVx > Math.abs(extensionRate)
      ) {
        kind = vx < 0 ? 'hook-left' : 'hook-right';
      } else if (
        extensionRate > JAB_EXTENSION_RATE &&
        absVx < absVy + extensionRate
      ) {
        kind = 'jab';
      } else if (extensionRate > JAB_EXTENSION_RATE) {
        kind = 'jab';
      } else if (lateralRatio > HOOK_LATERAL_RATIO) {
        kind = vx < 0 ? 'hook-left' : 'hook-right';
      }

      if (kind != null) {
        fireHit(kind, side, wrist);
      }
    }

    state.prevVel = { x: vx, y: vy };
    state.prevWristToShoulder = wristToShoulder;
    state.hasPrev = true;
  };

  const spawnTarget = (): void => {
    const idx = Math.min(TARGET_KINDS.length - 1, Math.floor(rand() * TARGET_KINDS.length));
    const kind = TARGET_KINDS[idx] ?? 'jab';
    currentTarget = { kind, spawnedAt: elapsed, hit: false };
  };

  const drawBag = (g: CanvasRenderingContext2D): void => {
    const cx = canvasW / 2;
    const cy = canvasH * 0.5;
    const w = Math.min(canvasW, canvasH) * 0.22;
    const h = w * 1.7;
    const top = cy - h * 0.55;

    g.save();
    g.fillStyle = '#2a1c14';
    g.fillRect(cx - 2, top - h * 0.18, 4, h * 0.18);
    g.fillStyle = '#6b7280';
    g.beginPath();
    g.arc(cx, top - h * 0.18, 6, 0, Math.PI * 2);
    g.fill();

    const grad = g.createLinearGradient(cx - w / 2, top, cx + w / 2, top);
    grad.addColorStop(0, '#2a1d12');
    grad.addColorStop(0.45, '#4a3528');
    grad.addColorStop(1, '#2a1d12');
    g.fillStyle = grad;
    g.beginPath();
    const radius = w * 0.18;
    g.moveTo(cx - w / 2 + radius, top);
    g.lineTo(cx + w / 2 - radius, top);
    g.quadraticCurveTo(cx + w / 2, top, cx + w / 2, top + radius);
    g.lineTo(cx + w / 2, top + h - radius);
    g.quadraticCurveTo(cx + w / 2, top + h, cx + w / 2 - radius, top + h);
    g.lineTo(cx - w / 2 + radius, top + h);
    g.quadraticCurveTo(cx - w / 2, top + h, cx - w / 2, top + h - radius);
    g.lineTo(cx - w / 2, top + radius);
    g.quadraticCurveTo(cx - w / 2, top, cx - w / 2 + radius, top);
    g.closePath();
    g.fill();

    g.strokeStyle = '#1a110a';
    g.lineWidth = 3;
    g.stroke();

    g.strokeStyle = '#1a110a';
    g.lineWidth = 2;
    for (let i = 1; i < 4; i++) {
      const y = top + (h * i) / 4;
      g.beginPath();
      g.moveTo(cx - w / 2, y);
      g.lineTo(cx + w / 2, y);
      g.stroke();
    }
    g.restore();
  };

  const drawTarget = (g: CanvasRenderingContext2D, kind: TargetKind): void => {
    const pos = targetPos(kind);
    const r = targetRadius();
    const color = targetColor(kind);
    const isActive = currentTarget != null && currentTarget.kind === kind && !currentTarget.hit;
    const img = PUNCH_SPRITES[kind];

    g.save();
    if (isActive) {
      const age = elapsed - currentTarget!.spawnedAt;
      const pulse = 0.5 + 0.5 * Math.sin(age * 12);
      g.shadowColor = color;
      g.shadowBlur = 28 + pulse * 16;
      if (img.complete && img.naturalWidth > 0) {
        const d = r * 2.4 * (0.95 + 0.06 * pulse);
        g.drawImage(img, pos.x - d / 2, pos.y - d / 2, d, d);
      } else {
        g.fillStyle = color;
        g.beginPath();
        g.arc(pos.x, pos.y, r * (0.9 + 0.08 * pulse), 0, Math.PI * 2);
        g.fill();
      }
    } else {
      g.globalAlpha = 0.4;
      if (img.complete && img.naturalWidth > 0) {
        const d = r * 2.2;
        g.drawImage(img, pos.x - d / 2, pos.y - d / 2, d, d);
      } else {
        g.strokeStyle = color;
        g.lineWidth = 3;
        g.beginPath();
        g.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        g.stroke();
      }
    }
    g.restore();

    // Label pill underneath so it stays readable over the sprite.
    g.save();
    const label =
      kind === 'jab'
        ? 'JAB'
        : kind === 'uppercut'
          ? 'UP'
          : 'HOOK';
    const fs = Math.round(r * 0.34);
    g.font = `bold ${fs}px system-ui, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    const labelY = pos.y + r * 0.95;
    const m = g.measureText(label);
    const padX = fs * 0.5;
    const padY = fs * 0.25;
    g.fillStyle = isActive ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.45)';
    g.fillRect(pos.x - m.width / 2 - padX, labelY - fs / 2 - padY, m.width + padX * 2, fs + padY * 2);
    g.fillStyle = isActive ? '#ffffff' : 'rgba(255,255,255,0.7)';
    g.fillText(label, pos.x, labelY);
    g.restore();
  };

  const drawTrail = (
    g: CanvasRenderingContext2D,
    state: HandState,
    color: string,
  ): void => {
    if (state.trail.length < 2) return;
    g.save();
    g.strokeStyle = color;
    g.lineCap = 'round';
    for (let i = 1; i < state.trail.length; i++) {
      const a = state.trail[i - 1];
      const b = state.trail[i];
      if (!a || !b) continue;
      const t = i / (state.trail.length - 1);
      g.globalAlpha = 0.15 + 0.75 * t;
      g.lineWidth = 4 + 10 * t;
      g.beginPath();
      g.moveTo(a.x, a.y);
      g.lineTo(b.x, b.y);
      g.stroke();
    }
    g.restore();

    const head = state.trail[state.trail.length - 1];
    if (head) {
      g.save();
      g.fillStyle = color;
      g.beginPath();
      g.arc(head.x, head.y, 10, 0, Math.PI * 2);
      g.fill();
      g.restore();
    }
  };

  const drawFlashes = (g: CanvasRenderingContext2D): void => {
    const maxR = targetRadius() * 1.8;
    for (const f of flashes) {
      const age = elapsed - f.startedAt;
      const t = age / 0.2;
      if (t < 0 || t > 1) continue;
      g.save();
      g.globalAlpha = 1 - t;
      g.strokeStyle = f.color;
      g.lineWidth = 6 * (1 - t * 0.5);
      g.beginPath();
      g.arc(f.x, f.y, maxR * t + 8, 0, Math.PI * 2);
      g.stroke();
      g.restore();
    }
  };

  const drawHud = (g: CanvasRenderingContext2D): void => {
    const fs = Math.max(18, Math.round(canvasH * 0.038));
    const pad = Math.round(canvasH * 0.04);

    g.save();
    g.textBaseline = 'bottom';
    g.font = `bold ${fs}px system-ui, sans-serif`;

    g.fillStyle = hits >= WIN_HITS ? '#22c55e' : '#ffffff';
    g.textAlign = 'left';
    g.fillText(`HITS: ${hits} / ${WIN_HITS}`, pad, canvasH - pad);

    g.fillStyle = combo > 1 ? '#fbbf24' : '#cbd5e1';
    g.textAlign = 'center';
    g.fillText(`COMBO: ${combo}`, canvasW / 2, canvasH - pad);

    const remain = Math.max(0, DURATION_SEC - elapsed);
    g.fillStyle = remain < 5 ? '#ef4444' : '#ffffff';
    g.textAlign = 'right';
    g.fillText(`${remain.toFixed(1)}s`, canvasW - pad, canvasH - pad);

    g.fillStyle = '#7d8298';
    g.font = `${Math.round(fs * 0.55)}px system-ui, sans-serif`;
    g.textAlign = 'center';
    g.textBaseline = 'top';
    g.fillText('jab • hook L/R • uppercut', canvasW / 2, pad);
    if (missed > 0) {
      g.fillStyle = '#9aa0bf';
      g.fillText(`missed: ${missed}`, canvasW / 2, pad + fs * 0.6);
    }
    g.restore();
  };

  const drawBackground = (g: CanvasRenderingContext2D): void => {
    g.fillStyle = '#11121a';
    g.fillRect(0, 0, canvasW, canvasH);
    const grad = g.createRadialGradient(
      canvasW / 2,
      canvasH * 0.5,
      Math.min(canvasW, canvasH) * 0.05,
      canvasW / 2,
      canvasH * 0.5,
      Math.min(canvasW, canvasH) * 0.7,
    );
    grad.addColorStop(0, 'rgba(255,255,255,0.05)');
    grad.addColorStop(1, 'rgba(0,0,0,0.45)');
    g.fillStyle = grad;
    g.fillRect(0, 0, canvasW, canvasH);
  };

  return {
    name: '09-boxing',
    durationSec: DURATION_SEC,
    requires: { hands: true, pose: true },

    init(c: GameContext): void {
      game = c;
      canvasW = c.canvas.width;
      canvasH = c.canvas.height;
      elapsed = 0;
      hits = 0;
      missed = 0;
      combo = 0;
      maxCombo = 0;
      currentTarget = null;
      lastTargetEndAt = -10;
      flashes.length = 0;
      lh = makeHandState();
      rh = makeHandState();
    },

    tick(dt: number, frame: InputFrame): void {
      if (game == null) return;
      canvasW = game.canvas.width;
      canvasH = game.canvas.height;
      elapsed += dt;

      if (
        currentTarget == null &&
        elapsed < DURATION_SEC &&
        elapsed - lastTargetEndAt > TARGET_COOLDOWN_SEC
      ) {
        spawnTarget();
      }

      if (currentTarget != null && elapsed - currentTarget.spawnedAt > TARGET_GLOW_SEC) {
        if (!currentTarget.hit) {
          missed++;
          combo = 0;
        }
        lastTargetEndAt = elapsed;
        currentTarget = null;
      }

      classifyPunch('left', frame, dt);
      classifyPunch('right', frame, dt);

      for (let i = flashes.length - 1; i >= 0; i--) {
        const f = flashes[i];
        if (!f) continue;
        if (elapsed - f.startedAt > 0.3) flashes.splice(i, 1);
      }
    },

    render(g: CanvasRenderingContext2D): void {
      if (game == null) return;
      drawBackground(g);
      drawBag(g);
      for (const k of TARGET_KINDS) drawTarget(g, k);
      drawFlashes(g);
      drawTrail(g, lh, '#22d3ee');
      drawTrail(g, rh, '#e879f9');
      drawHud(g);
    },

    end(): MicrogameResult {
      const won = hits >= WIN_HITS;
      return {
        microgame: '09-boxing',
        won,
        score: hits * 10 + maxCombo * 2,
        detail: { hits, missed, maxCombo },
      };
    },
  };
})();
